import { useContext } from 'react';
import { Button } from '../../../SubComponents/Buttons';
import { bookingState } from '../client-state/booking-state-context';
import useBookingChangeStep from '../../../Hooks/use-booking-change-step';
import { bookingStepDependencies } from '..';
import useLocalize from '../../../Hooks/useLocalize';
import { graphqlSdk } from '../../../Utils/graphql-client';
import { loadingReducerActionTypes } from '../../../redusers/loading-reducer';
import { bookingActionTypes } from '../client-state/reducers/bookingReducer';
import { orderReducerActionTypes } from '../../../redusers/order-reducer';
import { paymentReducerActionTypes } from '../../../redusers/payment-reducer';

const ReserveBookingButtonWithData = () => {
  const { state, dispatch } = useContext(bookingState);
  const localize = useLocalize();
  const changeStep = useBookingChangeStep(bookingStepDependencies);

  const updateBooking = async () => {
    if (state.booking.id && state.booking.date) {
      try {
        dispatch({ type: loadingReducerActionTypes.BOOKING, payload: true });
        const response = await graphqlSdk.booking_editBooking({
          boatCount: state.booking.boatCount,
          duration: state.booking.duration,
          date: state.booking.date,
          id: state.booking.id,
          pickupLocationId: state.booking.pickupLocationId,
          dropoffLocationId: state.booking.dropoffLocationId,
        });

        dispatch({
          type: bookingActionTypes.UPDATE_BOOKING,
          payload: {
            ...response.editBooking,
            lang: response.editBooking.lang ?? state.booking.lang,
          },
        });
        // if not using legacy payments, filter out items that are not valid for edited booking
        dispatch({
          type: orderReducerActionTypes.UPDATE_ORDER,
          payload: response.editBooking.addons.map((addon) => {
            return {
              quantity: addon.quantity,
              itemId: addon.itemId,
            };
          }),
        });
        /* TODO: Fix the any type  */
      } catch (error: any) {
        if (error?.response?.errors?.[0]?.extensions?.code === 'nothing-changed') {
          return dispatch({ type: bookingActionTypes.UPDATE_BOOKING, payload: {} });
        } else {
          throw error;
        }
      }
    }
  };

  const createPaymentIntent = async (bookingId: string) => {
    const paymentIntentResponse = await graphqlSdk.booking_createBookingPaymentIntent({
      bookingId: bookingId,
    });

    return dispatch({
      type: paymentReducerActionTypes.UPDATE_CLIENT_SECRET,
      payload: paymentIntentResponse.createBookingPaymentIntent,
    });
  };

  const createBooking = async () => {
    if (!state.booking.customerId && state.booking.date && state.booking.shopId) {
      try {
        dispatch({ type: loadingReducerActionTypes.BOOKING, payload: true });
        const response = await graphqlSdk.booking_reserve({
          date: state.booking.date,
          duration: state.booking.duration,
          boatCount: state.booking.boatCount,
          pickupLocationId: state.booking.pickupLocationId,
          dropoffLocationId: state.booking.dropoffLocationId,
          includeWarranty: state.isWarrantyApplied,
          shopId: state.booking.shopId,
          lang: state.booking.lang,
          customer: {
            firstname: state.customer.customer.firstname,
            lastname: state.customer.customer.lastname,
            email: state.customer.customer.email,
            phone: state.customer.customer.phone,
            address: state.customer.customer.address,
          },
        });

        // create payment intent, keep payment intent in state
        // don't await it though
        createPaymentIntent(response.reserve.id);

        return dispatch({
          type: bookingActionTypes.UPDATE_BOOKING,
          payload: {
            ...response.reserve,
            lang: response.reserve.lang ?? state.booking.lang,
          },
        });
      } catch (error) {
        throw error;
      }
    }
  };

  return (
    <Button
      loading={state.loading.booking}
      onClick={async () => {
        if (state.booking.date) {
          if (!state.booking.customerId) {
            await createBooking();
          } else if (state.booking.id) {
            await updateBooking();
          }

          changeStep(3);
        }
      }}
      className={!state.booking.date && 'disabled'}
      look="secondary"
      size="big"
      style={{ alignSelf: 'center' }}
    >
      {localize('product.next')}
    </Button>
  );
};

export default ReserveBookingButtonWithData;
